import { trackConfirmedLead, trackConfirmedNewsletter } from '@theme/lead-measurement';
/** Complete Shopify's server-issued challenge without navigating the storefront. */
async function completeShopifyChallenge(response, submittedBody, sourceForm) {
  sourceForm.dataset.submissionStage = 'opening-verification';
  const page = new DOMParser().parseFromString(await response.text(), 'text/html');
  const challengeForm = page.querySelector('#captcha_form');
  const initializer = [...page.scripts].find((script) => /hcaptcha\.render\(/.test(script.textContent));
  const sitekey = initializer?.textContent.match(/sitekey\s*:\s*["']([a-f0-9-]+)["']/i)?.[1];
  const challengeAction = new URL(challengeForm?.getAttribute('action') || '', response.url);
  sourceForm.dataset.challengeAction = challengeAction.pathname;
  // Shopify's challenge page restores the original form action from its saved
  // form record. Parsed HTML has not run that restoration script.
  const action = new URL(sourceForm.action, location.href);
  if (!challengeForm || !sitekey || action.origin !== location.origin || !window.hcaptcha?.render) {
    throw new Error('Verification could not be opened');
  }

  // The challenge response owns these fields, especially its authenticity token.
  // Preserve the original message, tags and form key, just as Shopify's challenge
  // page repopulates them from its form persistence script.
  const body = new URLSearchParams(submittedBody);
  for (const input of challengeForm.querySelectorAll('input[type="hidden"][name]')) {
    body.set(input.name, input.value);
  }
  body.delete('h-captcha-response');
  const returnPath = new URL(response.url).searchParams.get('return_path');
  if (returnPath) body.set('return_path', returnPath);

  const dialog = document.createElement('dialog');
  dialog.className = 'kc-verification-dialog';
  const heading = document.createElement('p');
  heading.id = `${sourceForm.id}-verification-title`;
  heading.textContent = page.querySelector('.shopify-challenge__message')?.textContent.trim();
  dialog.setAttribute('aria-labelledby', heading.id);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'kc-verification-dialog__close';
  close.setAttribute('aria-label', sourceForm.closest('[data-signup-error]').dataset.verificationClose);
  close.textContent = '×';
  const widget = document.createElement('div');
  dialog.append(close, heading, widget);
  document.body.append(dialog);
  // hCaptcha opens its puzzle in a body-level portal; a modal top layer
  // would make that portal inert. Keep this dialog non-modal.
  dialog.show();
  close.focus();

  return new Promise((resolve, reject) => {
    let widgetId;
    let finished = false;
    let posting = false;
    const finish = (result, error) => {
      if (finished) return;
      finished = true;
      if (widgetId !== undefined) window.hcaptcha.remove(widgetId);
      dialog.close();
      dialog.remove();
      sourceForm.querySelector('button[type="submit"], button:not([type])')?.focus({ preventScroll: true });
      if (error) reject(error);
      else resolve(result);
    };
    const fail = (reason = 'verification-cancelled') => finish(null, new Error(reason));
    close.addEventListener('click', () => fail());
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); if (!posting) fail(); });
    try {
      widgetId = window.hcaptcha.render(widget, {
        sitekey,
        callback: async (token) => {
          if (!token || finished || posting) return;
          posting = true;
          sourceForm.dataset.submissionStage = 'verification-submitting';
          body.set('h-captcha-response', token);
          dialog.setAttribute('aria-busy', 'true');
          close.disabled = true;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 30000);
          try {
            const verified = await fetch(action.href, {
              method: 'POST', body, credentials: 'same-origin', signal: controller.signal,
            });
            sourceForm.dataset.submissionStatus = String(verified.status);
            sourceForm.dataset.submissionPath = new URL(verified.url).pathname;
            finish(verified);
          } catch (error) {
            finish(null, error);
          } finally {
            clearTimeout(timeout);
          }
        },
        'error-callback': () => fail('verification-widget-error'),
        'expired-callback': () => fail('verification-expired'),
        'chalexpired-callback': () => fail('verification-challenge-expired'),
      });
    } catch (error) {
      finish(null, error);
    }
  });
}

function updateSignupStates() {
  const submittedFormId = window.location.hash.slice(1);

  for (const message of document.querySelectorAll('[data-signup-success]')) {
    const form = message.closest('form');
    if (!form) continue;

    const isSubmittedForm = form.dataset.signupComplete === 'true' || form.id === submittedFormId;
    message.hidden = !isSubmittedForm;
    message.style.display = isSubmittedForm ? '' : 'none';
    const fields = form.querySelector('[data-signup-fields]');
    if (fields) {
      fields.hidden = isSubmittedForm;
      fields.style.display = isSubmittedForm ? 'none' : '';
    }

    const input = form.querySelector('input[type="email"]');
    if (input && message.id) {
      if (isSubmittedForm) input.setAttribute('aria-describedby', message.id);
      else if (input.getAttribute('aria-describedby') === message.id) input.removeAttribute('aria-describedby');
    }
  }
}

const newsletterPendingKey = 'kaizen-newsletter-signup-pending';

function isFooterNewsletter(form) {
  return form.matches('.email-signup__form') && !!form.closest('.shopify-section-group-footer-group');
}

function rememberNewsletterSubmission(form) {
  if (!isFooterNewsletter(form)) return;
  rememberOutcome(newsletterPendingKey, form);
}

function outcomeReceipt(pending) { return pending.receipt || `${pending.id}:${pending.at}`; }
function rememberOutcome(key, form, type) {
  if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
  try {
    const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
    // Protection may replay the same submit event once. Keep its receipt.
    if (pending?.id === form.id && Date.now() - pending.at >= 0 && Date.now() - pending.at < 300000) return;
    sessionStorage.setItem(key, JSON.stringify({id: form.id, type, at: Date.now(),
      receipt: window.crypto?.randomUUID?.() || `${Date.now()}:${Math.random().toString(36).slice(2)}`}));
  } catch {}
}
function pendingReceipt(key, form) {
  try {
    const pending = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (pending?.id === form.id && Date.now() - pending.at >= 0 && Date.now() - pending.at < 300000) return outcomeReceipt(pending);
  } catch {}
  return window.crypto?.randomUUID?.() || `${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

function clearNewsletterSubmission(form) {
  if (!isFooterNewsletter(form)) return;
  try {
    const pending = JSON.parse(window.sessionStorage?.getItem(newsletterPendingKey) || 'null');
    if (pending?.id === form.id) window.sessionStorage?.removeItem(newsletterPendingKey);
  } catch {}
}

function trackNativeNewsletterSuccess() {
  if (new URLSearchParams(window.location.search).get('customer_posted') !== 'true') return;
  try {
    const pending = JSON.parse(window.sessionStorage?.getItem(newsletterPendingKey) || 'null');
    const age = Date.now() - pending?.at;
    if (!Number.isFinite(age) || age < 0 || age > 5 * 60 * 1000) {
      window.sessionStorage?.removeItem(newsletterPendingKey);
      return;
    }
    const form = document.getElementById(pending.id);
    if (!form || !isFooterNewsletter(form) || window.location.hash.slice(1) !== form.id || !form.querySelector('[data-signup-success]')) return;
    trackConfirmedNewsletter(outcomeReceipt(pending));
    window.sessionStorage?.removeItem(newsletterPendingKey);
  } catch {}
}

// Native redirect fallback: only a recent matching form submission may count.
const leadPendingKey = 'kaizen:lead-pending';
function formLeadType(form) {
  if (isFooterNewsletter(form)) return null;
  return form.matches('.kc-contact__form') ? 'contact' : form.closest('.coffee-club-signup') ? 'coffee_club' : null;
}
function rememberLeadSubmission(form) {
  const type = formLeadType(form);
  if (!type || window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
  rememberOutcome(leadPendingKey, form, type);
}
function clearLeadSubmission(form) {
  try {
    const pending = JSON.parse(sessionStorage.getItem(leadPendingKey) || 'null');
    if (pending?.id === form.id) sessionStorage.removeItem(leadPendingKey);
  } catch {}
}
function trackNativeLeadSuccess() {
  try {
    const pending = JSON.parse(sessionStorage.getItem(leadPendingKey) || 'null');
    if (!pending) return;
    const age = Date.now() - pending.at;
    if (!Number.isFinite(age) || age < 0 || age > 300000) { sessionStorage.removeItem(leadPendingKey); return; }
    const form = document.getElementById(pending.id);
    const param = pending.type === 'contact' ? 'contact_posted' : 'customer_posted';
    const success = pending.type === 'contact' ? '[data-contact-success]' : '[data-signup-success]';
    if (!form || formLeadType(form) !== pending.type || location.hash.slice(1) !== form.id ||
        new URLSearchParams(location.search).get(param) !== 'true' || !form.querySelector(success)) return;
    trackConfirmedLead(pending.type, outcomeReceipt(pending));
    sessionStorage.removeItem(leadPendingKey);
  } catch {}
}

const inlineSignupSelector = '.shopify-section-group-footer-group form.email-signup__form, .coffee-club-signup form, form.kc-contact__form';
const inlineSignupProtection = new WeakMap();

function initializeInlineSignup(form) {
  if (inlineSignupProtection.has(form)) return inlineSignupProtection.get(form);

  // Shopify captures this transport when protect() wraps form.submit(). It
  // still runs its own CAPTCHA first and includes its token in FormData.
  // If protection already bound, retain the native working submission path.
  if (!window.Shopify?.captcha?.protect || form.hasAttribute('data-hcaptcha-bound')) return;
  form.dataset.inlineSignupReady = 'true';
  const button = form.querySelector('button[type="submit"], button:not([type])');
  let pending = false;
  const showError = (text) => {
    clearNewsletterSubmission(form);
    clearLeadSubmission(form);
    let error = form.querySelector('[data-inline-signup-error]');
    if (!error) {
      error = document.createElement('p');
      error.dataset.inlineSignupError = '';
      error.id = `${form.id}-inline-error`;
      error.className = form.matches('.kc-contact__form') ? 'kc-contact__notice' : form.closest('.coffee-club-signup') ? 'coffee-club-signup__message' : 'email-signup__message';
      error.setAttribute('role', 'alert');
      form.append(error);
    }
    error.textContent = text || form.closest('[data-signup-error]').dataset.signupError;
    form.querySelector('input[type="email"]')?.setAttribute('aria-describedby', error.id);
  };

  let finishProtection;
  const state = { ready: new Promise(resolve => { finishProtection = resolve; }), queued: false, showError };
  inlineSignupProtection.set(form, state);
  const nativeSubmit = form.submit;
  const submitInline = async () => {
    if (!form.isConnected) return;
    // A programmatic submission during loading must also go through Shopify's
    // wrapper. Never run our transport before that wrapper is installed.
    if (!form.hasAttribute('data-hcaptcha-bound')) {
      if (await state.ready) form.submit();
      else showError();
      return;
    }
    if (pending || form.dataset.signupComplete === 'true') return;
    pending = true;
    form.dataset.submissionStage = 'submitting';
    delete form.dataset.submissionFailure;
    form.querySelector('[data-inline-signup-error]')?.remove();
    form.querySelector('input[type="email"]')?.removeAttribute('aria-describedby');
    form.setAttribute('aria-busy', 'true');
    const body = new URLSearchParams(new FormData(form));
    if (button) button.disabled = true;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      let response = await fetch(form.action, {
        method: 'POST', body, credentials: 'same-origin', signal: controller.signal,
      });
      let responseUrl = new URL(response.url);
      if (responseUrl.origin !== window.location.origin) throw new Error('Unexpected signup destination');
      clearTimeout(timeout);
      if (/\/challenge\/?$/.test(responseUrl.pathname)) {
        response = await completeShopifyChallenge(response, body, form);
        responseUrl = new URL(response.url);
        // Do not loop challenges or claim success after a second rejection.
        if (responseUrl.origin !== location.origin || /\/challenge\/?$/.test(responseUrl.pathname)) {
          throw new Error('Verification was not accepted');
        }
      }
      form.dataset.submissionStage = 'reading-confirmation';
      form.dataset.submissionStatus = String(response.status);
      form.dataset.submissionPath = responseUrl.pathname;
      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const responseForm = responseDocument.getElementById(form.id);
      const error = responseForm?.querySelector('[role="alert"]');
      if (error) { form.dataset.submissionFailure = 'server-form-error'; showError(error.textContent.trim()); return; }
      const isContact = form.matches('.kc-contact__form');
      const success = responseForm?.querySelector(isContact ? '[data-contact-success]' : '[data-signup-success]');
      if (!response.ok || (!isContact && responseUrl.searchParams.get('customer_posted') !== 'true') || !success) {
        throw new Error('Signup was not confirmed');
      }
      if (isFooterNewsletter(form)) {
        try { trackConfirmedNewsletter(pendingReceipt(newsletterPendingKey, form)); } catch { /* Measurement cannot block a confirmed signup. */ }
        clearNewsletterSubmission(form);
      }
      if (isContact || form.closest('.coffee-club-signup')) {
        trackConfirmedLead(isContact ? 'contact' : 'coffee_club', pendingReceipt(leadPendingKey, form));
        clearLeadSubmission(form);
      }
      form.querySelector('[data-signup-success]')?.remove();
      const confirmation = document.importNode(success, true);
      if (isContact) {
        form.replaceWith(confirmation);
      } else form.append(confirmation);
      form.dataset.signupComplete = 'true';
      updateSignupStates();
      confirmation.tabIndex = -1;
      confirmation.focus({ preventScroll: true });
    } catch (error) {
      form.dataset.submissionFailure = error instanceof Error ? error.message : 'unknown-error';
      showError();
    } finally {
      clearTimeout(timeout);
      pending = false;
      form.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  };
  form.submit = submitInline;
  const timeout = setTimeout(() => finishProtection(false), 60000);
  try {
    window.Shopify.captcha.protect(form, () => {
      clearTimeout(timeout);
      finishProtection(form.hasAttribute('data-hcaptcha-bound') && form.submit !== submitInline);
    });
  } catch {
    clearTimeout(timeout);
    form.submit = nativeSubmit;
    finishProtection(false);
  }
  return state;
}

function prepareInlineSignup(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const form = target.closest('form');
  if (!form?.matches(inlineSignupSelector)) return;
  if (event.type === 'submit') { rememberNewsletterSubmission(form); rememberLeadSubmission(form); }
  const state = initializeInlineSignup(form);
  if (event.type !== 'submit' || !state || form.hasAttribute('data-hcaptcha-bound')) return;

  // A fast tap, Enter, or requestSubmit can arrive before the provider downloads.
  // Resume the native validated submit event only after Shopify confirms binding.
  event.preventDefault();
  if (state.queued) return;
  state.queued = true;
  const button = event.submitter || form.querySelector('button[type="submit"], button:not([type])');
  const wasDisabled = button?.disabled;
  form.dataset.submissionStage = 'loading-verification';
  form.setAttribute('aria-busy', 'true');
  if (button) button.disabled = true;
  state.ready.then(ready => {
    state.queued = false;
    form.removeAttribute('aria-busy');
    if (button) button.disabled = wasDisabled;
    if (!form.isConnected) return;
    if (!ready) { state.showError(); return; }
    const submitter = event.submitter;
    form.requestSubmit(submitter?.form === form ? submitter : undefined);
  });
}

updateSignupStates();
trackNativeNewsletterSuccess();
trackNativeLeadSuccess();
document.addEventListener('shopify:section:load', updateSignupStates);
window.addEventListener('hashchange', updateSignupStates);
// Window capture runs before Shopify's document listeners so it can capture our
// inline transport. Delegation also covers forms inserted by the theme editor.
for (const event of ['pointerdown', 'focusin', 'change', 'submit']) {
  window.addEventListener(event, prepareInlineSignup, true);
}
